import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { get, post } from "@/lib/api";
import { StatePill } from "@/components/StatePill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { IllustrativeBadge } from "@/components/IllustrativeBadge";
import { toast } from "sonner";
import {
  ArrowRight, Check, Info, ShieldCheck, Loader2, SearchX, Clock, HelpCircle, Lock,
  Home, ClipboardList, FileText, Send, MessageSquarePlus, ChevronDown, PartyPopper, PauseCircle,
} from "lucide-react";

const STEPS = ["Welcome", "Your workspace", "Induction", "Policies", "Your access"];

export default function InvitePage() {
  const { token } = useParams();
  const [config, setConfig] = useState(null);
  const [invite, setInvite] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [requests, setRequests] = useState([]);
  const [state, setState] = useState("loading");

  const loadWallet = (wid) => get(`/mill/wallet/${wid}`).then(setWallet);
  const loadRequests = (wid) => get(`/mill/requests?wallet_id=${wid}`).then((d) => setRequests(d.requests));

  useEffect(() => {
    get("/mill/onboarding/config").then(setConfig);
    get(`/mill/invite/${token}`)
      .then(async (d) => {
        setInvite(d);
        if (d.progress?.wallet_id) { await loadWallet(d.progress.wallet_id); loadRequests(d.progress.wallet_id); }
        setState("ok");
      })
      .catch(() => setState("notfound"));
  }, [token]);

  // Derive the current linear phase from data (0..5).
  const phase = useMemo(() => {
    if (!wallet) return 0;
    if (wallet.suspended) return 99;
    if (wallet.role_active) return 5;
    if (!wallet.essential_info_complete) return 1;
    if (!wallet.induction_essentials.every((i) => i.done)) return 2;
    if (!wallet.policies.every((p) => p.acknowledged)) return 3;
    return 4; // ready for review / awaiting activation
  }, [wallet]);

  if (state === "loading") return <Centered><Loader2 className="h-5 w-5 animate-spin" /> Loading your invitation…</Centered>;
  if (state === "notfound") return (
    <Centered>
      <div className="text-center"><SearchX className="mx-auto h-8 w-8 text-mill-muted" />
        <p className="mt-3 font-semibold text-mill-ink">Invitation not found</p>
        <Link to="/journey/onboarding" className="mt-2 inline-block text-sm font-semibold text-mill-moss">Back to onboarding console</Link>
      </div>
    </Centered>
  );

  const blocked = ["revoked", "expired"].includes(invite.status);
  const stepIndex = Math.min(phase, 5);

  return (
    <div className="mx-auto max-w-3xl px-5 py-10 sm:px-8" data-testid="invite-page">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <IllustrativeBadge />
        {wallet && <StatePill state={wallet.activation_state === "active" ? "role_active" : wallet.activation_state} />}
      </div>

      {/* Progress stepper */}
      {wallet && !wallet.suspended && (
        <div className="mb-8 flex items-center gap-1" data-testid="onboarding-stepper">
          {STEPS.map((s, i) => (
            <div key={s} className="flex flex-1 items-center gap-1">
              <div className="flex flex-col items-center gap-1">
                <span className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold ${i < stepIndex ? "bg-mill-moss text-white" : i === stepIndex ? "bg-katlas-blue text-white" : "bg-stone-200 text-mill-muted"}`}>
                  {i < stepIndex ? <Check className="h-3.5 w-3.5" /> : i + 1}
                </span>
                <span className="hidden text-[10px] text-mill-muted sm:block">{s}</span>
              </div>
              {i < STEPS.length - 1 && <span className={`h-0.5 flex-1 ${i < stepIndex ? "bg-mill-moss" : "bg-stone-200"}`} />}
            </div>
          ))}
        </div>
      )}

      {/* Screen 1 — Welcome (before redeem) */}
      {phase === 0 && <Welcome invite={invite} config={config} blocked={blocked} token={token} onRedeemed={(w) => { setWallet(w); loadRequests(w.id); }} />}

      {phase === 99 && (
        <div className="rounded-3xl border border-[#FECACA] bg-[#FEF2F2] p-7 text-center" data-testid="screen-suspended">
          <PauseCircle className="mx-auto h-8 w-8 text-[#991B1B]" />
          <p className="mt-3 font-display text-lg font-semibold text-mill-ink">Your access is paused</p>
          <p className="mt-1 text-sm text-mill-muted">A member of the Mill team has paused your onboarding. They'll be in touch about next steps.</p>
        </div>
      )}

      {config && wallet && phase >= 1 && phase <= 4 && (
        <>
          {phase === 1 && <Workspace config={config} wallet={wallet} onSaved={setWallet} />}
          {phase === 2 && <Induction wallet={wallet} onChange={setWallet} />}
          {phase === 3 && <Policies wallet={wallet} onChange={setWallet} />}
          {phase === 4 && <Activation wallet={wallet} config={config} />}
        </>
      )}

      {config && wallet && phase === 5 && (
        <MillHome wallet={wallet} config={config} requests={requests} onRequested={() => loadRequests(wallet.id)} />
      )}
    </div>
  );
}

function Centered({ children }) {
  return <div className="mx-auto flex max-w-3xl items-center gap-2 px-5 py-24 text-mill-muted">{children}</div>;
}

// --------------------------------------------------------------------------- //
function Welcome({ invite, config, blocked, token, onRedeemed }) {
  const [busy, setBusy] = useState(false);
  const [showFormats, setShowFormats] = useState(false);
  const redeem = async () => {
    setBusy(true);
    try {
      const w = await post(`/mill/invite/${token}/redeem`, {});
      onRedeemed(w);
      toast.success("Great — let's get you set up.");
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "This invitation cannot be redeemed.");
    }
    setBusy(false);
  };
  return (
    <div className="rounded-3xl border border-stone-200 bg-white p-7 sm:p-9" data-testid="screen-welcome">
      <p className="text-sm font-semibold uppercase tracking-[0.16em] text-mill-moss">Welcome to The Mill</p>
      <h1 className="mt-2 font-display text-3xl font-bold text-mill-ink sm:text-4xl">Hello {invite.display_name}</h1>
      <p className="mt-3 text-lg text-mill-muted">You've been invited to begin the <b className="capitalize text-mill-ink">{invite.role}</b> onboarding pathway.</p>

      <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Fact icon={ShieldCheck} label="Your role" value={invite.role} />
        <Fact icon={Clock} label="Invitation expires" value={invite.expiry_date} />
        <Fact icon={ClipboardList} label="Time needed" value={config?.estimated_time || "About 10 minutes"} />
      </div>

      <button onClick={() => setShowFormats((s) => !s)} className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-mill-moss" data-testid="need-format-toggle">
        <HelpCircle className="h-4 w-4" /> Need a different format? <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showFormats ? "rotate-180" : ""}`} />
      </button>
      {showFormats && (
        <div className="mt-2 flex flex-wrap gap-2" data-testid="format-options">
          {(config?.alternative_formats || []).map((f) => (
            <span key={f} className="rounded-full border border-stone-200 bg-mill-alt px-3 py-1 text-xs text-mill-muted">{f}</span>
          ))}
        </div>
      )}

      {blocked ? (
        <p className="mt-6 rounded-xl bg-[#FEE2E2] px-4 py-3 text-sm text-[#991B1B]" data-testid="invite-blocked">This invitation is {invite.status} and can no longer be used.</p>
      ) : (
        <Button className="mt-7 h-12 bg-mill-moss px-7 text-base hover:bg-mill-moss-dark" onClick={redeem} disabled={busy} data-testid="start-onboarding">
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Start onboarding <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      )}
      <p className="mt-4 flex items-start gap-1.5 text-xs text-mill-muted"><Info className="mt-0.5 h-3.5 w-3.5 shrink-0" /> This invitation starts your onboarding pathway. It does not by itself grant permanent access — you'll gain your role once you've completed the steps.</p>
    </div>
  );
}
function Fact({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl border border-stone-200 bg-mill-alt/40 p-3">
      <span className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-mill-muted"><Icon className="h-3 w-3" /> {label}</span>
      <span className="mt-0.5 block font-medium capitalize text-mill-ink">{value}</span>
    </div>
  );
}

// --------------------------------------------------------------------------- //
function Workspace({ config, wallet, onSaved }) {
  const [resp, setResp] = useState(wallet.essential_info || {});
  const [busy, setBusy] = useState(false);
  const [tech, setTech] = useState(false);
  const submit = async () => {
    setBusy(true);
    const w = await post(`/mill/wallet/${wallet.id}/essential-info`, { responses: resp });
    onSaved(w);
    toast.success("Workspace set up.");
    setBusy(false);
  };
  return (
    <div className="rounded-3xl border border-stone-200 bg-white p-7" data-testid="screen-workspace">
      <h2 className="font-display text-2xl font-bold text-mill-ink">Set up your Mill workspace</h2>
      <p className="mt-2 text-mill-muted">This is your private place for role-relevant guidance, tasks, requests and confirmation of completed steps.</p>
      <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
        {config.essential_info_fields.map((f) => (
          <div key={f.id}>
            <Label className="text-xs text-mill-muted">{f.label}</Label>
            {f.type === "select" ? (
              <select data-testid={`essential-${f.id}`} className="h-10 w-full rounded-md border border-input bg-white px-3 text-sm" value={resp[f.id] || ""} onChange={(e) => setResp({ ...resp, [f.id]: e.target.value })}>
                <option value="">Select…</option>{f.options.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : (
              <Input data-testid={`essential-${f.id}`} value={resp[f.id] || ""} onChange={(e) => setResp({ ...resp, [f.id]: e.target.value })} />
            )}
          </div>
        ))}
      </div>
      <p className="mt-4 flex items-start gap-1.5 rounded-xl bg-mill-alt px-3 py-2 text-xs text-mill-muted"><Info className="mt-0.5 h-3.5 w-3.5 shrink-0" /> Illustrative form — synthetic data only. We never collect safeguarding, health, DBS, protected-characteristic, payment, emergency-contact or real identity data here.</p>

      <button onClick={() => setTech((t) => !t)} className="mt-4 inline-flex items-center gap-1.5 text-xs font-medium text-mill-muted hover:text-mill-ink" data-testid="how-it-works-toggle">
        How this works (technical) <ChevronDown className={`h-3.5 w-3.5 transition-transform ${tech ? "rotate-180" : ""}`} />
      </button>
      {tech && (
        <div className="mt-2 rounded-xl border border-katlas-blue/30 bg-katlas-navy p-4 text-xs text-white/80" data-testid="wallet-technical">
          <p className="font-mono">{wallet.wallet_label} · {wallet.mode}</p>
          <p className="mt-1 text-white/60">Behind the scenes a synthetic managed cloud wallet was provisioned when you started. Identity: {wallet.identity}. It contains no real personal information and no keys or addresses are shown. In a future pilot this maps to a managed cloud guest wallet.</p>
        </div>
      )}
      <Button className="mt-5 bg-mill-moss hover:bg-mill-moss-dark" onClick={submit} disabled={busy} data-testid="workspace-submit">Continue <ArrowRight className="ml-1.5 h-4 w-4" /></Button>
    </div>
  );
}

// --------------------------------------------------------------------------- //
function Induction({ wallet, onChange }) {
  const [busy, setBusy] = useState(false);
  const toggle = async (item) => {
    setBusy(true);
    const w = await post(`/mill/wallet/${wallet.id}/induction`, { item_id: item.id, done: !item.done });
    onChange(w);
    setBusy(false);
  };
  const done = wallet.induction_essentials.filter((i) => i.done).length;
  return (
    <div className="rounded-3xl border border-stone-200 bg-white p-7" data-testid="screen-induction">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-2xl font-bold text-mill-ink">Induction essentials</h2>
        <span className="text-sm text-mill-muted">{done}/{wallet.induction_essentials.length}</span>
      </div>
      <p className="mt-2 text-mill-muted">Short reads you can complete in stages.</p>
      <div className="mt-5 space-y-2">
        {wallet.induction_essentials.map((i) => (
          <label key={i.id} data-testid={`induction-${i.id}`} className={`flex cursor-pointer items-center gap-3 rounded-xl border p-3.5 ${i.done ? "border-[#A7F3D0] bg-[#F0FDF4]" : "border-stone-200"}`}>
            <Checkbox checked={i.done} disabled={busy} onCheckedChange={() => toggle(i)} data-testid={`induction-check-${i.id}`} />
            <span className={`text-sm ${i.done ? "text-mill-ink" : "text-mill-muted"}`}>{i.label}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------- //
function Policies({ wallet, onChange }) {
  const [busy, setBusy] = useState(false);
  const ack = async (p) => {
    setBusy(true);
    const w = await post(`/mill/wallet/${wallet.id}/acknowledge-policy`, { policy_id: p.id });
    onChange(w);
    toast.success(`Acknowledged: ${p.title}`);
    setBusy(false);
  };
  return (
    <div className="rounded-3xl border border-stone-200 bg-white p-7" data-testid="screen-policies">
      <h2 className="font-display text-2xl font-bold text-mill-ink">Policy acknowledgement</h2>
      <p className="mt-2 text-mill-muted">Please read and acknowledge the policies relevant to your role.</p>
      <div className="mt-5 space-y-3">
        {wallet.policies.map((p) => (
          <div key={p.id} data-testid={`policy-${p.id}`} className={`rounded-2xl border p-5 ${p.acknowledged ? "border-[#A7F3D0] bg-[#F0FDF4]" : "border-stone-200 bg-white"}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="font-display font-semibold text-mill-ink">{p.title}</h3>
                <p className="mt-1 text-sm text-mill-muted">{p.summary}</p>
                <p className="mt-2 text-xs text-mill-muted">Owner: {p.owner} · Version {p.version} · Reviewed {p.last_reviewed}</p>
              </div>
              {p.acknowledged && <Check className="h-5 w-5 shrink-0 text-mill-moss" />}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <Link to={p.link} className="text-xs font-semibold text-mill-moss hover:underline">Read full content</Link>
              {!p.acknowledged && <Button size="sm" className="bg-mill-moss hover:bg-mill-moss-dark" disabled={busy} onClick={() => ack(p)} data-testid={`ack-policy-${p.id}`}>Acknowledge</Button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------- //
function Activation({ wallet, config }) {
  const reviewMode = config.activation_mode !== "automatic";
  return (
    <div className="space-y-6" data-testid="screen-activation">
      <div className="rounded-3xl border border-stone-200 bg-white p-7 text-center">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[#DBEAFE] text-katlas-blue"><Clock className="h-6 w-6" /></div>
        <h2 className="mt-4 font-display text-2xl font-bold text-mill-ink">Ready for Mill review</h2>
        <p className="mt-2 text-mill-muted">
          You've completed your onboarding. {reviewMode ? "A member of the Mill team will review and activate your role shortly." : "Your role is being activated."}
        </p>
        <p className="mt-1 text-sm text-mill-muted">Next step: your <b>Volunteer Lead / Centre Administrator</b> will confirm activation. You'll then see the services available for your role.</p>
      </div>
      <div>
        <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-mill-muted">Available once your role is active</p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="locked-cards">
          {config.volunteer_services.map((s) => (
            <div key={s.id} className="flex items-center gap-2 rounded-xl border border-dashed border-stone-300 bg-mill-alt/40 px-4 py-3 text-sm text-mill-muted" data-testid={`locked-card-${s.id}`}>
              <Lock className="h-3.5 w-3.5" /> {s.label}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------- //
function MillHome({ wallet, config, requests, onRequested }) {
  const [target, setTarget] = useState(null);
  return (
    <div className="space-y-6" data-testid="mill-home">
      <div className="rounded-3xl border border-[#A7F3D0] bg-[#F0FDF4] p-6" data-testid="activation-confirm">
        <div className="flex items-center gap-2"><PartyPopper className="h-5 w-5 text-[#065F46]" /><h2 className="font-display text-xl font-bold text-mill-ink">You are now active as a Mill Volunteer</h2></div>
        <p className="mt-1 text-sm text-mill-muted">Your workspace now includes the services and requests available for your role.</p>
      </div>

      <div className="rounded-2xl border border-stone-200 bg-white p-6">
        <div className="flex items-center gap-2"><Home className="h-5 w-5 text-mill-moss" /><h3 className="font-display text-lg font-semibold text-mill-ink">My Mill Home — Welcome, {wallet.display_name}</h3></div>
        <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
          {config.volunteer_services.map((s) => s.route ? (
            <Link key={s.id} to={s.route} data-testid={`service-${s.id}`} className="flex items-center justify-between rounded-lg border border-stone-200 px-4 py-3 text-sm text-mill-ink hover:bg-mill-alt">
              {s.label} <ArrowRight className="h-3.5 w-3.5 text-mill-moss" />
            </Link>
          ) : (
            <button key={s.id} data-testid={`service-${s.id}`} onClick={() => setTarget(s.request_to)} className="flex items-center justify-between rounded-lg border border-stone-200 px-4 py-3 text-sm text-mill-ink hover:bg-mill-alt">
              {s.label} <MessageSquarePlus className="h-3.5 w-3.5 text-mill-moss" />
            </button>
          ))}
        </div>
        {target && <RequestComposer config={config} wallet={wallet} target={target} onClose={() => setTarget(null)} onDone={() => { setTarget(null); onRequested(); }} />}
      </div>

      <MyRequests requests={requests} />
      <Completions wallet={wallet} />
    </div>
  );
}

function RequestComposer({ config, wallet, target, onClose, onDone }) {
  const cfg = config.request_purposes[target];
  const [selection, setSelection] = useState("");
  const [fields, setFields] = useState({});
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (cfg.kind === "choice" && !selection) { toast.error("Please choose an option."); return; }
    setBusy(true);
    await post("/mill/requests", { wallet_id: wallet.id, to_role: target, selection: selection || null, fields: cfg.kind === "form" ? fields : null });
    toast.success(`Request sent to the ${cfg.to_role_label}.`);
    setBusy(false);
    onDone();
  };
  return (
    <div className="mt-5 rounded-xl border border-mill-moss/40 bg-mill-alt/40 p-5" data-testid="request-composer">
      <p className="text-sm font-semibold text-mill-ink">To the {cfg.to_role_label}</p>
      <p className="text-xs text-mill-muted">Purpose: “{cfg.purpose}”</p>
      {cfg.kind === "choice" ? (
        <div className="mt-3 space-y-1.5">
          {cfg.choices.map((c) => (
            <label key={c} className="flex items-center gap-2 text-sm text-mill-ink">
              <input type="radio" name="sel" value={c} checked={selection === c} onChange={() => setSelection(c)} data-testid={`req-choice-${c.slice(0, 10).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} /> {c}
            </label>
          ))}
        </div>
      ) : (
        <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
          {cfg.fields.map((f) => (
            <div key={f.id}>
              <Label className="text-xs text-mill-muted">{f.label}</Label>
              {f.type === "select" ? (
                <select data-testid={`req-field-${f.id}`} className="h-9 w-full rounded-md border border-input bg-white px-2 text-sm" value={fields[f.id] || ""} onChange={(e) => setFields({ ...fields, [f.id]: e.target.value })}>
                  <option value="">Select…</option>{f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : (
                <Input data-testid={`req-field-${f.id}`} type={f.type === "number" ? "number" : "text"} value={fields[f.id] || ""} onChange={(e) => setFields({ ...fields, [f.id]: e.target.value })} className="h-9" />
              )}
            </div>
          ))}
        </div>
      )}
      <div className="mt-4 flex gap-2">
        <Button size="sm" className="bg-mill-moss hover:bg-mill-moss-dark" onClick={submit} disabled={busy} data-testid="request-send"><Send className="mr-1.5 h-3.5 w-3.5" /> Send request</Button>
        <Button size="sm" variant="outline" onClick={onClose}>Cancel</Button>
      </div>
    </div>
  );
}

function MyRequests({ requests }) {
  if (!requests.length) return null;
  return (
    <div className="rounded-2xl border border-stone-200 bg-white p-6" data-testid="my-requests">
      <h3 className="font-display text-lg font-semibold text-mill-ink">My requests</h3>
      <div className="mt-3 space-y-2">
        {requests.map((r) => (
          <div key={r.id} className="flex items-center justify-between gap-3 rounded-xl border border-stone-200 px-4 py-3" data-testid={`my-request-${r.id}`}>
            <div><p className="text-sm text-mill-ink">→ {r.to_role_label}</p><p className="text-xs text-mill-muted">{r.selection || r.purpose}</p></div>
            <StatePill state={r.status} />
          </div>
        ))}
      </div>
    </div>
  );
}

function Completions({ wallet }) {
  if (!wallet.completions?.length) return null;
  return (
    <div className="rounded-2xl border border-dashed border-katlas-receipt/60 bg-[#FFFDF5] p-6" data-testid="completions">
      <div className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-[#B45309]" /><h3 className="font-display text-lg font-semibold text-mill-ink">Your confirmations</h3></div>
      <div className="mt-3 space-y-3">
        {wallet.completions.map((c, i) => (
          <dl key={i} className="grid grid-cols-2 gap-x-4 gap-y-1.5 rounded-xl border border-stone-200 bg-white p-4 text-xs sm:grid-cols-3" data-testid={`completion-${i}`}>
            {[["Role", c.role], ["Action", c.action], ["Status", c.status], ["Policy version", c.policy_version], ["Issued by", c.issued_by], ["Timestamp", c.timestamp]].map(([k, v]) => (
              <div key={k}><dt className="text-[10px] uppercase tracking-wider text-mill-muted">{k}</dt><dd className="font-medium capitalize text-mill-ink">{v}</dd></div>
            ))}
            <div className="col-span-2 flex items-center gap-1.5 border-t border-stone-200 pt-2 text-[11px] font-semibold text-[#B45309] sm:col-span-3"><Info className="h-3.5 w-3.5" /> Verification: {c.verification} — not a cryptographic receipt in v0.3.</div>
          </dl>
        ))}
      </div>
    </div>
  );
}
